import React, { useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

interface CsvOptionImportProps {
  onImport: (options: string[], weights?: Record<string, number>, mode?: 'replace' | 'append') => void
  existingOptionsCount: number
}

const HEADER_PATTERNS = ['option', 'label', 'name', 'country', 'value', 'choice', 'item']
const MAX_FILE_SIZE = 100 * 1024 // 100KB
const MAX_OPTIONS = 500
const MAX_OPTION_LENGTH = 200

type ImportMode = null | 'replace' | 'append'

const CsvOptionImport: React.FC<CsvOptionImportProps> = ({ onImport, existingOptionsCount }) => {
  const intl = useIntl()
  const fileRef = useRef<HTMLInputElement>(null)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [pendingData, setPendingData] = useState<{
    options: string[]
    weights?: Record<string, number>
    duplicatesRemoved: number
  } | null>(null)

  const clearMessages = () => {
    setError(null)
    setSuccess(null)
  }

  const parseCsv = (text: string): { options: string[]; weights?: Record<string, number>; duplicatesRemoved: number } => {
    const lines = text.split(/\r?\n/)
    const options: string[] = []
    const weights: Record<string, number> = {}
    let hasWeights = false
    let duplicatesRemoved = 0
    const seen = new Set<string>()

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim()
      if (!line) continue

      // Parse CSV respecting quoted values
      const cols = parseCsvLine(line)
      const label = cols[0]?.trim()
      if (!label) continue

      // Skip header row
      if (i === 0 && HEADER_PATTERNS.includes(label.toLowerCase())) continue

      // Check option length
      if (label.length > MAX_OPTION_LENGTH) {
        throw new Error(intl.formatMessage({ id: 'csv.error_option_length' }, { line: i + 1, max: MAX_OPTION_LENGTH }))
      }

      // Deduplicate (case-insensitive)
      const key = label.toLowerCase()
      if (seen.has(key)) {
        duplicatesRemoved++
        continue
      }
      seen.add(key)

      options.push(label)

      // Parse weight from second column
      if (cols.length >= 2) {
        const w = parseFloat(cols[1]?.trim())
        if (!isNaN(w)) {
          hasWeights = true
          weights[label] = w
        }
      }
    }

    return { options, weights: hasWeights ? weights : undefined, duplicatesRemoved }
  }

  const parseCsvLine = (line: string): string[] => {
    const result: string[] = []
    let current = ''
    let inQuotes = false

    for (let i = 0; i < line.length; i++) {
      const ch = line[i]
      if (ch === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"'
          i++
        } else {
          inQuotes = !inQuotes
        }
      } else if (ch === ',' && !inQuotes) {
        result.push(current)
        current = ''
      } else {
        current += ch
      }
    }
    result.push(current)
    return result
  }

  const handleFile = (file: File) => {
    clearMessages()

    if (file.size > MAX_FILE_SIZE) {
      setError(intl.formatMessage({ id: 'csv.error_file_too_large' }, { size: Math.round(file.size / 1024) }))
      return
    }

    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const text = e.target?.result as string
        const result = parseCsv(text)

        if (result.options.length === 0) {
          setError(intl.formatMessage({ id: 'csv.error_no_options' }))
          return
        }

        if (result.options.length > MAX_OPTIONS) {
          setError(intl.formatMessage({ id: 'csv.error_too_many' }, { count: result.options.length, max: MAX_OPTIONS }))
          return
        }

        // If existing options, ask replace or append
        if (existingOptionsCount > 0) {
          setPendingData(result)
        } else {
          finishImport(result, 'replace')
        }
      } catch (err: any) {
        setError(err.message || intl.formatMessage({ id: 'csv.error_parse' }))
      }
    }
    reader.onerror = () => setError(intl.formatMessage({ id: 'csv.error_read' }))
    reader.readAsText(file, 'UTF-8')
  }

  const finishImport = (data: typeof pendingData, mode: ImportMode) => {
    if (!data) return
    setPendingData(null)

    onImport(data.options, data.weights, mode || 'replace')

    const msg = intl.formatMessage({ id: 'csv.imported' }, { count: data.options.length })
    const extra = data.duplicatesRemoved > 0
      ? intl.formatMessage({ id: 'csv.imported_duplicates' }, { count: data.duplicatesRemoved })
      : ''
    setSuccess(msg + extra)
    setTimeout(() => setSuccess(null), 4000)
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) handleFile(file)
    // Reset so same file can be re-selected
    e.target.value = ''
  }

  return (
    <div className="inline-flex flex-col">
      <input
        ref={fileRef}
        type="file"
        accept=".csv,.txt"
        onChange={handleChange}
        className="hidden"
      />

      <button
        type="button"
        className="text-sm text-foreground-muted hover:text-primary"
        onClick={() => { clearMessages(); fileRef.current?.click() }}
      >
        <FormattedMessage id="csv.import_button" />
      </button>

      {/* Replace/Append dialog */}
      {pendingData && (
        <div className="mt-2 p-3 rounded-lg border border-border bg-surface text-sm">
          <p className="text-foreground mb-2">
            <FormattedMessage id="csv.found_options" values={{ found: pendingData.options.length, existing: existingOptionsCount }} />
          </p>
          <div className="flex gap-2">
            <button
              className="px-3 py-1 rounded bg-primary text-white text-xs font-medium hover:bg-primary-dark"
              onClick={() => finishImport(pendingData, 'replace')}
            >
              <FormattedMessage id="csv.replace_all" />
            </button>
            <button
              className="px-3 py-1 rounded bg-surface border border-border text-foreground text-xs font-medium hover:bg-background"
              onClick={() => finishImport(pendingData, 'append')}
            >
              <FormattedMessage id="csv.append" />
            </button>
            <button
              className="px-3 py-1 text-xs text-foreground-muted hover:text-foreground"
              onClick={() => setPendingData(null)}
            >
              <FormattedMessage id="csv.cancel" />
            </button>
          </div>
        </div>
      )}

      {error && (
        <p className="mt-1 text-xs text-error">{error}</p>
      )}
      {success && (
        <p className="mt-1 text-xs text-success">{success}</p>
      )}
    </div>
  )
}

export default CsvOptionImport
